import {type RefCallback, type RefObject, useCallback, useMemo, useRef} from 'react'

export type ElementRef = {
  /** Read the element. */
  ref: RefObject<HTMLElement | null>
  /** Spread onto whichever element the caller chose. */
  setRef: RefCallback<HTMLElement>
}

/**
 * A ref object to read from, and a callback ref to hand out in props.
 *
 * The board's hooks return props a caller spreads onto whatever element it likes, and a
 * `RefObject<HTMLElement>` cannot be spread onto a `<div>`: a ref object is invariant in
 * its element type, so a board that returned one would force every caller to pick the
 * same tag the board happened to think of. A callback ref has no such problem, because a
 * function that accepts any element accepts a div.
 */
export function useElementRef(): ElementRef {
  const ref = useRef<HTMLElement | null>(null)

  const setRef = useCallback<RefCallback<HTMLElement>>(element => {
    ref.current = element
  }, [])

  return useMemo(() => ({ref, setRef}), [setRef])
}
